import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Sparkles, FileText, Calendar, Palette, Brain, Loader2 } from "lucide-react";
import { toast } from "sonner";
import DashboardShell from "@/pages/dashboard/DashboardShell";
import { useAuth } from "@/lib/auth";
import {
  ARCHETYPE_LABELS,
  ARCHETYPE_BLURBS,
  ARCHETYPES,
  createProject,
  draftWithBuilder,
} from "@/lib/vault";
import { extractFormError } from "@/lib/api";
import ProjectPreview from "@/pages/dashboard/ProjectPreview";

const ARCHETYPE_ICONS = {
  lead_capture: FileText,
  content_pack: Calendar,
  brand_kit: Palette,
  recommender: Brain,
};

const ARCHETYPE_PLACEHOLDERS = {
  lead_capture:
    "Example: a booking form for my Brookline med-spa for new Botox clients. Ask for name, email, phone, preferred date and the treatment they're curious about.",
  content_pack:
    "Example: a 7-day Instagram + email plan promoting our spring capsule launch. Warm, confident tone. Mix product shots, behind-the-scenes and a launch-day push.",
  brand_kit:
    "Example: a brand kit for a boutique financial-coaching firm for working moms. Trustworthy, warm and slightly playful. Earthy gold + deep navy palette.",
  recommender:
    "Example: a 3-question quiz that recommends the right yoga membership tier (drop-in, monthly or unlimited) based on practice frequency and goals.",
};

export default function StudioBuilderPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isPending = (user?.plan || "pending") === "pending" && user?.role !== "admin";

  const [archetype, setArchetype] = useState("lead_capture");
  const [prompt, setPrompt] = useState("");
  const [name, setName] = useState("");
  const [drafting, setDrafting] = useState(false);
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);

  const handleDraft = async () => {
    if (isPending) {
      toast.error("Pick a plan to unlock the Studio Builder.");
      return;
    }
    if (prompt.trim().length < 8) {
      toast.error("Tell the Studio a little more about what you want to build.");
      return;
    }
    if (drafting) return;
    setDrafting(true);
    setDraft(null);
    try {
      const data = await draftWithBuilder({ archetype, prompt: prompt.trim() });
      setDraft(data.config);
      if (!name) {
        const fallback =
          data.config?.headline ||
          data.config?.title ||
          data.config?.brand_name ||
          data.config?.topic ||
          `${ARCHETYPE_LABELS[archetype]} draft`;
        setName(String(fallback).slice(0, 80));
      }
      toast.success("Draft ready. Review below and save to your Vault.");
    } catch (err) {
      toast.error(extractFormError(err, "The Studio Builder is taking a moment. Try again."));
    } finally {
      setDrafting(false);
    }
  };

  const handleSave = async () => {
    if (!draft) return;
    if (!name.trim()) {
      toast.error("Give your project a name first.");
      return;
    }
    setSaving(true);
    try {
      const project = await createProject({
        name: name.trim(),
        archetype,
        config: draft,
      });
      toast.success(`${project.name} saved to your Vault.`);
      navigate(`/dashboard/vault/${project.id}`);
    } catch (err) {
      toast.error(extractFormError(err, "Couldn't save that project."));
      setSaving(false);
    }
  };

  return (
    <DashboardShell back="/dashboard/vault" backLabel="Vault">
      <section data-testid="builder-hero">
        <p className="overline">Studio Builder</p>
        <h1 className="font-serif text-4xl sm:text-5xl tracking-tight leading-[1.05] mt-4">
          Describe it. <span className="italic text-aurora">We&apos;ll draft it.</span>
        </h1>
        <p className="text-neutral-500 mt-4 max-w-2xl leading-relaxed">
          Pick what you want to build, then tell the Studio what you need in plain English.
          We&apos;ll draft a deployable starting point you can edit and publish.
        </p>
      </section>

      {/* Archetype picker */}
      <section className="mt-10 sm:mt-12">
        <p className="overline">1. Pick what to build</p>
        <div
          data-testid="builder-archetypes"
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-4"
        >
          {ARCHETYPES.map((a) => {
            const Icon = ARCHETYPE_ICONS[a];
            const active = archetype === a;
            return (
              <button
                key={a}
                type="button"
                onClick={() => {
                  setArchetype(a);
                  setDraft(null);
                }}
                data-testid={`builder-archetype-${a}`}
                aria-pressed={active}
                className={`text-left border p-5 transition ${
                  active
                    ? "border-gold bg-gold/[0.06]"
                    : "border-neutral-200 bg-white hover:border-[#D4AF37]/50"
                }`}
              >
                <Icon size={18} className={active ? "text-gold" : "text-neutral-500"} strokeWidth={1.4} />
                <h3 className={`font-serif text-lg mt-4 ${active ? "text-gold" : "text-neutral-900"}`}>
                  {ARCHETYPE_LABELS[a]}
                </h3>
                <p className="text-xs text-neutral-500 mt-2 leading-relaxed">{ARCHETYPE_BLURBS[a]}</p>
              </button>
            );
          })}
        </div>
      </section>

      {/* Prompt */}
      <div className="mt-10 sm:mt-12">
        <p className="overline">2. Tell the Studio what you need</p>
        <textarea
          data-testid="builder-prompt"
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          placeholder={ARCHETYPE_PLACEHOLDERS[archetype]}
          rows={6}
          className="mt-4 w-full bg-white border border-neutral-200 focus:border-gold focus:outline-none p-5 text-sm leading-relaxed text-neutral-900 placeholder:text-neutral-400 resize-y"
          maxLength={2000}
        />
        <div className="flex items-center justify-between mt-3">
          <span className="text-xs text-neutral-400">{prompt.length} / 2000</span>
          <button
            type="button"
            onClick={handleDraft}
            data-testid="builder-draft-cta"
            disabled={drafting || isPending}
            className="btn-aurora disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {drafting ? (
              <>
                <Loader2 size={14} className="animate-spin" /> Drafting...
              </>
            ) : (
              <>
                <Sparkles size={14} /> Draft this project
              </>
            )}
          </button>
        </div>
        {isPending && (
          <p className="text-xs text-neutral-500 mt-3" data-testid="builder-paywall-hint">
            Studio Builder activates when your plan does.{" "}
            <a href="/pricing" className="text-gold hover:text-gold underline-offset-4 hover:underline">
              Pick a plan
            </a>
            .
          </p>
        )}
      </div>

      {/* Draft preview */}
      {draft && (
        <section data-testid="builder-draft" className="mt-12 sm:mt-16 border-t border-neutral-200/70 pt-10">
          <div className="flex items-end justify-between flex-wrap gap-5">
            <div>
              <p className="overline">3. Review the draft</p>
              <h2 className="font-serif text-3xl mt-3">
                Looks good? <span className="italic text-aurora">Save it.</span>
              </h2>
            </div>
            <div className="flex items-center gap-3 flex-wrap">
              <input
                type="text"
                data-testid="builder-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Project name"
                maxLength={80}
                className="bg-white border border-neutral-200 focus:border-gold focus:outline-none px-4 py-2.5 text-sm w-72"
              />
              <button
                type="button"
                onClick={handleSave}
                data-testid="builder-save-cta"
                disabled={saving}
                className="btn-aurora disabled:opacity-60"
              >
                {saving ? (
                  <>
                    <Loader2 size={14} className="animate-spin" /> Saving...
                  </>
                ) : (
                  "Save to Vault"
                )}
              </button>
            </div>
          </div>

          <div className="mt-8">
            <ProjectPreview archetype={archetype} config={draft} />
          </div>
        </section>
      )}
    </DashboardShell>
  );
}
